import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { useCreateRecipeTag } from '../api/recipe-tag-mutations';

interface AddRecipeTagDialogProps {
  open: boolean;
  onClose: () => void;
}

export function AddRecipeTagDialog({ open, onClose }: AddRecipeTagDialogProps) {
  const { tenantId } = useParams({
    from: '/_authenticated/$tenantId/settings/recipe-tags',
  });

  const [tagName, setTagName] = useState('');
  const createTag = useCreateRecipeTag();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tagName.trim()) return;

    try {
      await createTag.mutateAsync([
        {
          title: tagName.trim(),
          organisation_id: tenantId,
        },
      ]);
      setTagName('');
      onClose();
    } catch {
      toast.error('Failed to create tag');
    }
  };

  const handleClose = () => {
    setTagName('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Recipe Tag</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="tagName" className="text-sm font-medium">
              Tag Name
            </label>
            <Input
              id="tagName"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Enter tag name (e.g., Breakfast, Vegetarian)"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!tagName.trim() || createTag.isPending}
            >
              {createTag.isPending ? 'Creating...' : 'Create Tag'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
